/**
 * <section-carousel>
 *
 * Turns the direct children of a section's content wrapper into a horizontal
 * scroll-snap carousel with dot navigation.
 *
 * Written as a dependency-free custom element on purpose: it is rendered from
 * snippets/section.liquid, which is used by every generic section in the theme,
 * so it must not rely on the theme's module graph or hydration order.
 *
 * Markup (see snippets/section.liquid):
 *
 *   <section-carousel data-carousel-mode="mobile" data-carousel-dots="true">
 *     <div class="jp-carousel__track ...">…blocks…</div>
 *     <ul class="jp-carousel__dots" data-carousel-dots-list></ul>
 *   </section-carousel>
 *
 * data-carousel-mode:
 *   "mobile"  – carousel below 750px, normal section layout above
 *   "always"  – carousel at every viewport width
 *
 * The element sets data-carousel-active="true|false" on itself; all layout is
 * driven from that attribute in custom.css, so the section falls back to the
 * theme's own layout the moment the carousel is inactive or JS fails.
 */

const MOBILE_QUERY = '(max-width: 749px)';

class SectionCarousel extends HTMLElement {
  #track = null;
  #dotsList = null;
  #mediaQuery = null;
  #resizeObserver = null;
  #mutationObserver = null;
  #scrollFrame = 0;
  #activeIndex = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-carousel-track]');
    this.#dotsList = this.querySelector('[data-carousel-dots-list]');

    if (!this.#track) return;

    this.#mediaQuery = window.matchMedia(MOBILE_QUERY);
    this.#mediaQuery.addEventListener('change', this.#handleViewportChange);
    this.#track.addEventListener('scroll', this.#handleScroll, { passive: true });

    // Blocks can be added, removed or reordered in the theme editor.
    this.#mutationObserver = new MutationObserver(() => this.#refresh());
    this.#mutationObserver.observe(this.#track, { childList: true });

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#refresh());
      this.#resizeObserver.observe(this.#track);
    }

    // Theme editor: scroll to the block the merchant just selected.
    this.addEventListener('shopify:block:select', this.#handleBlockSelect);

    this.#refresh();
  }

  disconnectedCallback() {
    this.#mediaQuery?.removeEventListener('change', this.#handleViewportChange);
    this.#track?.removeEventListener('scroll', this.#handleScroll);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    this.#mutationObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#scrollFrame);
  }

  /** Slides are the track's element children, minus anything hidden. */
  get #slides() {
    if (!this.#track) return [];

    return Array.from(this.#track.children).filter(
      (child) => child instanceof HTMLElement && !child.hasAttribute('hidden')
    );
  }

  get #isActive() {
    if (this.dataset.carouselMode === 'always') return true;

    return Boolean(this.#mediaQuery?.matches);
  }

  #handleViewportChange = () => this.#refresh();

  #handleScroll = () => {
    cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => this.#syncActiveDot());
  };

  #handleBlockSelect = (event) => {
    if (!this.#isActive) return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const slide = this.#slides.find((candidate) => candidate === target || candidate.contains(target));
    if (slide) this.#scrollToSlide(slide, 'smooth');
  };

  #refresh() {
    const active = this.#isActive;

    this.dataset.carouselActive = String(active);

    const slides = this.#slides;

    // A single slide is not a carousel.
    if (!active || slides.length < 2) {
      this.dataset.carouselActive = 'false';
      this.#renderDots(0);
      return;
    }

    this.#renderDots(slides.length);
    this.#syncActiveDot();
  }

  #renderDots(count) {
    if (!this.#dotsList) return;

    if (this.dataset.carouselDots === 'false') count = 0;

    if (this.#dotsList.children.length === count) return;

    this.#dotsList.replaceChildren();

    for (let index = 0; index < count; index += 1) {
      const item = document.createElement('li');
      const dot = document.createElement('button');

      dot.type = 'button';
      dot.className = 'jp-carousel__dot';
      dot.dataset.carouselDot = String(index);
      dot.setAttribute('aria-label', `${index + 1}`);
      dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      dot.addEventListener('click', () => {
        const slide = this.#slides[index];
        if (slide) this.#scrollToSlide(slide, 'smooth');
      });

      item.append(dot);
      this.#dotsList.append(item);
    }
  }

  /** Marks the dot for whichever slide sits closest to the centre of the track. */
  #syncActiveDot() {
    if (!this.#track || !this.#dotsList || this.dataset.carouselActive !== 'true') return;

    const slides = this.#slides;
    if (slides.length < 2) return;

    const trackRect = this.#track.getBoundingClientRect();
    const trackCentre = trackRect.left + trackRect.width / 2;

    let closestIndex = 0;
    let closestDistance = Infinity;

    slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - trackCentre);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    if (closestIndex === this.#activeIndex && this.#dotsList.querySelector('[aria-current="true"]')) return;

    this.#activeIndex = closestIndex;

    this.#dotsList.querySelectorAll('[data-carousel-dot]').forEach((dot, index) => {
      dot.setAttribute('aria-current', index === closestIndex ? 'true' : 'false');
    });
  }

  /**
   * Scrolls the track only — never the page. `scrollIntoView` would drag the
   * whole document sideways on some browsers, so the offset is applied by hand.
   *
   * An absolute `scrollTo` target is used rather than `scrollBy`: a relative
   * scroll issued while a previous smooth scroll is still animating lands in
   * the wrong place, which is easy to trigger by tapping two dots in a row.
   */
  #scrollToSlide(slide, behavior) {
    if (!this.#track) return;

    const trackRect = this.#track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const delta = slideRect.left + slideRect.width / 2 - (trackRect.left + trackRect.width / 2);

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#track.scrollTo({
      left: this.#track.scrollLeft + delta,
      behavior: prefersReducedMotion ? 'auto' : behavior,
    });
  }
}

if (!customElements.get('section-carousel')) {
  customElements.define('section-carousel', SectionCarousel);
}
